import { useState } from 'react'
import { FormInput } from '../../../components/forms/input/FormInput'
import { FormSelect } from '../../../components/forms/select/FormSelect'
import { FormSelectAutocomplete } from '../../../components/forms/select/FormSelectAutocomplete'
import { Modal } from 'react-bootstrap'
import {
  Control,
  Controller,
  FieldErrors,
  SubmitHandler,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormResetField,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { AppConstantes } from '../../../utils/Constantes'

import { TTareaForm } from '../models/tareaForm.type'
import {
  useLazyRecuperarSitioAutocompleteQuery,
  useLazyRecuperarAutorizacionesPorSitioQuery,
} from '../../../redux/api'
import {
  OpcionSitiosSelect,
  OpcionesSelectAutorizaciones,
  OpcionUsuarioSelect,
} from '../../../components/forms/select/OpcionesSelect'
import { Alert } from '../../../components/alerts/Alert'
import { FormReactDatePicker } from '../../../components/forms/datePicker/FormReactDatePicker'
import { useLazyRecuperarUsuarioAutocompleteQuery } from '../../../redux/api/usuarios.api'
import { Cargando } from '../../../components/layout/Cargando'
import { Tarea } from '../models'
import { Usuario } from '../../usuarios/models'
import { Roles } from '../../../utils/Roles'
import useHasPermiso from '../../../hooks/autenticacion/useHasPermiso'
import { InputEstatus } from './InputEstatus'
import { Recordatorios } from '../../recordatorios/Recordatorios'
import { Autorizacion } from '../../autorizaciones/models'
import { Sitio } from '../../sitios/models'

type Props = {
  tarea?: Tarea
  condicionantes?: { etiqueta: string; valor: string }[]
  claveNivelTarea?: string
  isModoEditar: boolean
  isLoadingGuardar: boolean
  isLoadingActualizar: boolean
  recuperarCondicionantesPorIdAutorizacion: (arg: Autorizacion) => void
  setIdSitio: (arg: string) => void
  idSitio?: string
  onSubmit: SubmitHandler<TTareaForm>
  handleSubmit: UseFormHandleSubmit<TTareaForm>
  refetch: () => void
  register: UseFormRegister<TTareaForm>
  errors: FieldErrors<TTareaForm>
  control: Control<TTareaForm>
  resetField: UseFormResetField<TTareaForm>
  getValues: UseFormGetValues<TTareaForm>
  trigger: UseFormTrigger<TTareaForm>
  watch: UseFormWatch<TTareaForm>
}

const OPCIONES_BLOQUEADAS = 'BAJA'

export const ModalTareaBody = ({
  tarea,
  condicionantes,
  claveNivelTarea,
  isModoEditar,
  isLoadingGuardar,
  isLoadingActualizar,
  recuperarCondicionantesPorIdAutorizacion,
  setIdSitio,
  idSitio,
  handleSubmit,
  onSubmit,
  refetch,
  watch,
  register,
  errors,
  control,
  resetField,
  getValues,
  trigger,
}: Props) => {
  const watchEstatus = watch('estatus')
  const { hasPermiso } = useHasPermiso()
  const usuarioPermiso = hasPermiso([Roles.roleTareasCrear])
  const [autorizaciones, setAutorizaciones] = useState([])
  const [totalRecordatorios, setTotalRecordatorios] = useState(0)
  const [recuperarSitios] = useLazyRecuperarSitioAutocompleteQuery()
  const [recuperarAutorizaciones] = useLazyRecuperarAutorizacionesPorSitioQuery()
  const [recuperarUsuarios] = useLazyRecuperarUsuarioAutocompleteQuery()

  /**
   * @description Agrega las opciones al select de autocompletado
   *              La búsqueda se ejecuta a partir de 2 caracteres para reducir llamadas.
   *
   * Nota: si el usuario borra texto o escribe menos de 2 chars no se invoca API.
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 07/12/2022
   * @param entradaBusqueda
   * @param callback Regresa el arreglo de opciones del select
   */
  const agregaOpcionesSelect = (entradaBusqueda: string, callback: Function) => {
    if (entradaBusqueda && entradaBusqueda.length >= 2) {
      recuperarSitios(entradaBusqueda)
        .unwrap()
        .then((resp: Sitio[]) => {
          callback(
            resp?.map((sitio: Sitio) => ({
              etiqueta: sitio.nombre,
              valor: sitio.idSitio,
              descRazonSocial: sitio.descRazonSocial,
              rfc: sitio.rfc,
            }))
          )
        })
        .catch(() => console.error)
    }
  }

  /**
   * @description Agrega las opciones al select de autocompletado
   *              El autocomplete de usuarios depende del sitio seleccionado.
   *
   * Contrato: el endpoint recibe `{ nombre, idSitio }`. Si `idSitio` es undefined,
   * el backend decidirá el comportamiento (No identificado en el codigo/configuracion revisada.).
   *
   * @author andres.martinez, Hábil MX
   * @version 1.0.0, 07/12/2022
   * @param entradaBusqueda
   * @param callback Regresa el arreglo de opciones del select
   */
  const agregaOpcionesSelectUsuario = (entradaBusqueda: string, callback: Function) => {
    if (entradaBusqueda && entradaBusqueda.length >= 2) {
      recuperarUsuarios({ nombre: entradaBusqueda, idSitio: idSitio })
        .unwrap()
        .then((resp) => {
          callback(
            resp?.map((usuario: Usuario) => ({
              etiqueta: `${usuario.nombre} ${usuario.apellidoPaterno} ${usuario.apellidoMaterno}`,
              valor: usuario.idUsuario,
              correo: usuario.correo,
              descPosicion: usuario.descPosicion,
            }))
          )
        })
        .catch(console.error)
    }
  }

  /**
   * @description recupera todas las autorizaciones relacionadas a un sitio
   *
   * Side effects: actualiza estado local `autorizaciones` (opciones del select)
   * y propaga `idSitio` al contenedor.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/02/2023
   * @param idSitio del sitio seleccionadO
   */
  const recuperarAutorizacionesPorIdSitio = (idSitio: string) => {
    recuperarAutorizaciones(idSitio)
      .unwrap()
      .then((resp: any) => {
        const autorizaciones = resp.map((autorizacion: Autorizacion) => ({
          etiqueta: autorizacion.descAutorizacion,
          valor: autorizacion.idAutorizacion,
          numero: autorizacion.numero,
        }))
        setAutorizaciones(autorizaciones)
      })
      .catch(() => console.error)
    setIdSitio(idSitio)
  }

  return (
    <Modal.Body className={`${usuarioPermiso ? '' : 'overlay overlay-allowed'}`}>
      <form id='form-tarea' onSubmit={handleSubmit(onSubmit)}>
        <div
          className={`${isLoadingGuardar || isLoadingActualizar ? 'overlay overlay-block' : ''}`}>
          <div className='row'>
            <div className='col-12 mb-4'>
              <h4>Datos generales</h4>
            </div>
            <div className='col-4'>
              <Controller
                control={control}
                name='idSitio'
                render={({ field: { onChange, value } }) => (
                  <FormSelectAutocomplete
                    // Al editar o cuando la tarea proviene de un contexto (autorizacion/condicionante),
                    // se bloquea el sitio para no romper las relaciones existentes.
                    isDisabled={isModoEditar || claveNivelTarea !== undefined}
                    autoFocus={true}
                    value={value}
                    nombre='Sitio'
                    nombreCampo='idSitio'
                    errors={errors}
                    isObligatorio={true}
                    cargaOpciones={agregaOpcionesSelect}
                    onChange={(e: {
                      etiqueta?: string
                      valor: string
                      descRazonSocial?: string
                      razonSocial?: string
                      rfc?: string
                    }) => {
                      onChange(e)
                      // Dependencias de catálogo: autorización/usuario/estatus dependen del sitio.
                      // Por eso al cambiar sitio se resetean esos campos.
                      recuperarAutorizacionesPorIdSitio(getValues('idSitio.valor'))
                      resetField('idAutorizacion')
                      resetField('idUsuarioAsignado')
                      resetField('estatus')
                      setIdSitio(e?.valor)
                    }}
                    placeholder='Selecciona un sitio'
                    Componente={OpcionSitiosSelect}
                  />
                )}
              />
            </div>
            <div className='col-4'>
              <Controller
                control={control}
                name='idAutorizacion'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    isDisabled={
                      isModoEditar || !getValues('idSitio') || claveNivelTarea !== undefined
                    }
                    value={value}
                    nombreCampo='idAutorizacion'
                    nombre='Autorización'
                    elementos={autorizaciones}
                    onChange={(e: { valor: string }) => {
                      onChange(e)
                      // Los condicionantes dependen de la autorización seleccionada.
                      recuperarCondicionantesPorIdAutorizacion(getValues('idAutorizacion.valor'))
                      resetField('idCondicionante')
                    }}
                    placeholder='Selecciona una autorización'
                    Componente={OpcionesSelectAutorizaciones}
                  />
                )}
              />
            </div>
            <div className='col-4'>
              <Controller
                control={control}
                name='idCondicionante'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    isDisabled={
                      isModoEditar || !getValues('idAutorizacion') || claveNivelTarea !== undefined
                    }
                    value={value}
                    nombreCampo='idCondicionante'
                    nombre='Condicionante'
                    elementos={condicionantes}
                    onChange={(e: Event) => {
                      onChange(e)
                    }}
                    placeholder={
                      claveNivelTarea !== undefined
                        ? 'Campo inhabilitado'
                        : 'Selecciona una condicionante'
                    }
                  />
                )}
              />
            </div>
            <div className='col-12 mb-5' />
            <div className='col-4'>
              <FormInput
                className='mb-5'
                nombre='Título'
                nombreCampo='titulo'
                isObligatorio={true}
                register={register}
                errors={errors}
                placeholder='Ej. Transporte de residuos'
                maxLength={100}
                disabled={tarea?.estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
              />
            </div>
            <div className='col-7'>
              <FormInput
                className='mb-5'
                nombreCampo='descripcion'
                nombre='Descripción'
                isObligatorio={true}
                register={register}
                errors={errors}
                placeholder='Ej. Puesta en marcha de las instalaciones'
                maxLength={200}
                disabled={tarea?.estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
              />
            </div>
            <div className='col-2'>
              <InputEstatus
                control={control}
                errors={errors}
                isModoEditar={isModoEditar}
                tareaId={tarea}
                filtro={false}
              />
            </div>
            <div className='col-4'>
              <Controller
                control={control}
                name='idUsuarioAsignado'
                render={({ field: { onChange, value } }) => (
                  <FormSelectAutocomplete
                    className='mb-5'
                    isDisabled={
                      !getValues('idSitio') ||
                      tarea?.estatus === OPCIONES_BLOQUEADAS ||
                      !usuarioPermiso
                    }
                    nombre='Usuario asignado'
                    nombreCampo='idUsuarioAsignado'
                    errors={errors}
                    isObligatorio={true}
                    cargaOpciones={agregaOpcionesSelectUsuario}
                    onChange={(e: Event) => {
                      onChange(e)
                    }}
                    placeholder='Ej. Ana Martín'
                    Componente={OpcionUsuarioSelect}
                    value={value}
                  />
                )}
              />
            </div>
            <div className='col-3'>
              <label className='form-label fs-4'>
                Fecha de inicio<span className='text-danger'> *</span>
              </label>
              <Controller
                control={control}
                name='fechaInicio'
                render={({ field: { value, onChange } }) => (
                  <FormReactDatePicker
                    maxDate={
                      getValues('fechaTermino') ? new Date(getValues('fechaTermino')) : undefined
                    }
                    // Validación adicional en UI: evita elegir inicio > termino.
                    required={true}
                    name='fechaInicio '
                    dateFormat='dd/MMM/yyyy'
                    placeholderText='Ej. 22/03/2022'
                    onChange={onChange}
                    isClearable={true}
                    value={value}
                    popperPlacement='auto'
                    disabled={tarea?.estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
                  />
                )}
              />
              {errors && errors?.fechaInicio && (
                <p className='text-danger text-end fs-5'>{errors?.fechaInicio?.message}</p>
              )}
            </div>
            <div className='col-3'>
              <label className='form-label fs-4'>
                Fecha de termino<span className='text-danger'> *</span>
              </label>
              <Controller
                control={control}
                name='fechaTermino'
                render={({ field: { value, onChange } }) => (
                  <FormReactDatePicker
                    required={true}
                    name='fechaTermino'
                    dateFormat='dd/MMM/yyyy'
                    placeholderText='Ej. 22/03/2022'
                    onChange={onChange}
                    isClearable={true}
                    value={value}
                    popperPlacement='auto'
                    disabled={tarea?.estatus === OPCIONES_BLOQUEADAS || !usuarioPermiso}
                  />
                )}
              />
              {errors && errors?.fechaTermino && (
                <p className='text-danger text-end fs-5'>{errors?.fechaTermino?.message}</p>
              )}
            </div>
            <div className='col-12 border-bottom' />
          </div>
          <div className='row'>
            <div className='col-2 my-5'>
              <h4 className='d-inline'>Recordatorios </h4>
              <span className='text-muted fs-4'>| {totalRecordatorios} total</span>
            </div>
          </div>
          {watchEstatus?.valor === 'CANCELADA' ? (
            <Alert
              icono='fa-solid fa-triangle-exclamation fs-2x'
              mensaje='Al actualizar una tarea con estatus cancelada, todos sus recordatorios y alertas son eliminados'
              color='neon-carrot'
              titulo='Tarea cancelada'
            />
          ) : (
            <Recordatorios
              isModoEditar={isModoEditar}
              // Para tareas, la fecha de referencia para calculo de recordatorios suele ser el termino.
              // El backend decide cómo interpreta `fechaReferencia` para cada unidad de recordatorio.
              fechaReferencia={tarea?.fechaTermino}
              idRelacionado={tarea?.idTarea}
              tipo={AppConstantes.CVE_RECORDATORIO_TAREA}
              setTotalRecordatorios={setTotalRecordatorios}
              actualizaRecordatorios={refetch}
              estatus={tarea?.estatus}
              {...{
                getValues,
                trigger,
                watch,
                control,
                register,
                errors,
              }}
            />
          )}
          {(isLoadingGuardar || isLoadingActualizar) && <Cargando mensaje='Guardando tarea' />}
        </div>
      </form>
    </Modal.Body>
  )
}
